import { Modal } from "@fero/components";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { PRODUCT_PRICE_UPLOAD_CSV_TEMPLATE_URL } from "components/assets/url";
import {
  FilePicker,
  RemovableFileItem,
} from "components/library/widgets/file-picker";
import { Link } from "components/widgets/link/link";
import type React from "react";
import type { RowIdentifierAndErrors, WithCsvRowNumber } from "utils/csv-utils";
import type { ProductSalePriceRowData } from "../hooks/product-sale-price-csv-parser";

interface ImportPriceListModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: () => Promise<void>;
  parseErrors: WithCsvRowNumber<RowIdentifierAndErrors>[];
  parsedRowData: WithCsvRowNumber<ProductSalePriceRowData>[];
  selectedFile?: File;
  onSelectedFileChange: (file?: File) => Promise<void>;
}

const ParseError: React.FC<{
  error: WithCsvRowNumber<RowIdentifierAndErrors>;
}> = ({ error }) => {
  return (
    <Stack direction="row" justifyContent={"space-between"}>
      <Typography>{error.rowNumber}</Typography>
      <Typography>{error.value.errors.join(" | ")}</Typography>
    </Stack>
  );
};

export const ImportPriceListModal: React.FC<ImportPriceListModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  parseErrors,
  parsedRowData,
  selectedFile,
  onSelectedFileChange,
}) => {
  const hasParseErrors = parseErrors.length > 0;
  const hasSuccessfulParseData = parsedRowData.length > 0;
  return (
    <Modal
      isOpen={isOpen}
      header="Import Price List"
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitDisabled={hasParseErrors || !hasSuccessfulParseData}
      size="md"
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={0.6}>
          <Link href={PRODUCT_PRICE_UPLOAD_CSV_TEMPLATE_URL}>Click here</Link>
          <Typography fontWeight={400} fontSize={16}>
            to download the CSV price list template
          </Typography>
        </Stack>

        <FilePicker
          accept={"text/csv"}
          onFilesAccepted={async (files: File[]) => {
            const [file] = files;
            if (file) {
              onSelectedFileChange(file);
            }
          }}
          maxFileCount={1}
        />
        {selectedFile && (
          <RemovableFileItem
            file={selectedFile}
            onClick={() => onSelectedFileChange(undefined)}
          />
        )}
        {hasParseErrors && (
          <Stack spacing={2}>
            <Alert variant="filled" color="error">
              <Typography>
                Failed to Read CSV - row numbers & errors below
              </Typography>
            </Alert>
            <Box overflow={"auto"} maxHeight={"20vh"}>
              {parseErrors.map((e) => (
                <ParseError key={e.rowNumber} error={e} />
              ))}
            </Box>
          </Stack>
        )}

        {hasSuccessfulParseData && (
          <Alert variant="filled" color="success">
            <Typography>
              Succesfully Read CSV - click submit to complete import
            </Typography>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
};
